import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type CellDoubleClickedEvent, type CellStyle } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import gridInputCellEditor from "@/components/aggrid/aggrid_input_cell_editor";
import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import PopupCommon from "@components/popup/popup_common";
import SearchInputPopup from "@user/views/stock/popup_search_input";
import StockHistoryPopup from "@user/views/stock/popup_stock_history";
import StockAddPopup from "@user/views/stock/popup_stock_add";

import PrintLabel, { type PrintLabelHandle } from "@user/views/print/print_label";

import inputIcon from "@user/assets/images/common/table-basic-secondary.svg";

interface State {
  gridApi: GridApi | null,
  isShowSearchInput: boolean,
  isShowStockAdd: boolean,
  skuNo: string,
  isShowStockHistory: boolean,
  isHoverBtn1: boolean,
  isHoverBtn2: boolean,
  isHoverBtn3: boolean,
  isHoverBtn4: boolean,
  workStationId: string
}

const defState: State = {
  gridApi: null,
  isShowSearchInput: false,
  isShowStockAdd: false,
  skuNo: "",
  isShowStockHistory: false,
  isHoverBtn1: false,
  isHoverBtn2: false,
  isHoverBtn3: false,
  isHoverBtn4: false,
  workStationId: ""
};

interface SearchParams {
  wh: string,
  pns: string,
  pnsSummary: string,
  trNos: string,
  trNosSummary: string,
  sns: string,
  snsSummary: string,
  skuNoExs: string,
  skuNoExsSummary: string,
  mngType: string,
  stockUpdYn: string,
  locationIdFrom: string,
  locationFrom1: string,
  locationFrom2: string,
  locationFrom3: string,
  locationFrom4: string,
  locationIdTo: string,
  locationTo1: string,
  locationTo2: string,
  locationTo3: string,
  locationTo4: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  wh: "",
  pns: "",
  pnsSummary: "",
  trNos: "",
  trNosSummary: "",
  sns: "",
  snsSummary: "",
  skuNoExs: "",
  skuNoExsSummary: "",
  mngType: "",
  stockUpdYn: "",
  locationIdFrom: "",
  locationFrom1: "",
  locationFrom2: "",
  locationFrom3: "",
  locationFrom4: "",
  locationIdTo: "",
  locationTo1: "",
  locationTo2: "",
  locationTo3: "",
  locationTo4: "",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const validationMsg4reqSkuPick = [
  {id: "reqInfos", type: "NotBlank", msg: "지정출고 재고를 선택해 주세요."},
  {id: "workStationId", type: "NotBlank", msg: "출고작업대를 선택해 주세요."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."}
];

const validationMsg4updateStockQty = [
  {id: "(.*).stockQty", type: "NotNull", msg: "Stock Qty를 입력해 주세요."},
  {id: "(.*).stockQty", type: "Min", msg: "Stock Qty를 확인해 주세요."},
  {id: "(.*).stockQty", type: "Max", msg: "Stock Qty를 확인해 주세요."},
  {id: "reqInfos", type: "NotBlank", msg: "수정할 재고가 없습니다."}
];

const defColDef = { ...kutil.defaultColDef, cellDataType: false };

function StockStatus(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const printLabelRef = useRef<PrintLabelHandle>(null);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const tmpCellStyle1: CellStyle = {textAlign: "right"};
  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 150, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "창고", field: "wh", width: 80, cellStyle: { textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 110, cellStyle: tmpCellStyle1, valueFormatter: valueFormatter.number,
      cellClass: (p: any) => (p.node.rowPinned ? ["ta-right"] : ["input-cell", "ta-right"]),
      summable: true, filter: true, filterParams: {buttons: ["reset"]},
      editable: (p: any) => !p.node.rowPinned,
      singleClickEdit: true,
      cellEditor: gridInputCellEditor,
      cellEditorParams: { maxLength: 7 }
    },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Location", field: "locationId", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.locationId, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "시효정보", field: "expireDate", width: 150, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.date("yyyy-MM-dd") },
    { headerName: "관리구분", field: "mngType", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("mngType") },
    { headerName: "재고수정여부", field: "stockUpdYn", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("yn") },
    { headerName: "최근셀수정일", field: "recentCellUpdDate", width: 180, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
    },
    selectionColumnDef: {
      resizable: true
    },
    stopEditingWhenCellsLoseFocus: true,
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onCellDoubleClicked: (e: CellDoubleClickedEvent) => {
      if (e.node.rowPinned || (e.colDef.cellStyle as any)?.cursor != "pointer") {
        return;
      }
      openPopupStockHistory(e.data.skuNo);
    }
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!isActiveTab || state.isShowSearchInput || state.isShowStockAdd || state.isShowStockHistory) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [isActiveTab, state.isShowSearchInput, state.isShowStockAdd, state.isShowStockHistory]);
  // 끝: 바코드 스캔 기능 적용

  // 작업대ID 기본값 지정
  useEffect(() => {
    const workStationId = useSettingsStore.getState().getDefWorkStationId(location.path) || (commonCode.workStationId as any[])?.[0]?.code || "";
    setState(prev => ({ ...prev, workStationId}));
  }, [location.path]);

  // 변경 작업대ID 저장
  useEffect(() => {
    if (state.workStationId) {
      useSettingsStore.getState().setLocalSetting("workStationId", location.path, state.workStationId);
    }
  }, [state.workStationId]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams, ["locationFrom1", "locationFrom2", "locationFrom3", "locationFrom4", "locationTo1", "locationTo2", "locationTo3", "locationTo4"]);
      const data = await kutil.sendGet("/api/stock/stock_status.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/stock/stock_status", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/stock/stock_status", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/stock/stock_status", {});
  }
  
  function changeLocation(e: any) {
    const name = e.target.name as string;
    const value = e.target.value as string;
    const isFrom = name.startsWith("locationFrom");
    const sort = Number(name.slice(-1));
    const TOTAL_LEVELS = 4;

    const next: any = { ...searchParams };
    const counter = isFrom ? `locationTo${sort}` : `locationFrom${sort}`;

    next[name] = value;

    if (value === "") {
      next[counter] = "";
      for (let i = sort + 1; i <= TOTAL_LEVELS; i++) {
        next[`locationFrom${i}`] = "";
        next[`locationTo${i}`] = "";
      }
    } else {
      if (next[counter] === "") {
        next[counter] = value;
      }
      for (let i = 1; i < sort; i++) {
        const fromKey = `locationFrom${i}`;
        const toKey = `locationTo${i}`;
        if (next[fromKey] === "") next[fromKey] = "1";
        if (next[toKey] === "") next[toKey] = "1";
      }
    }

    if (sort === 1 && value !== "") {
      const prefix = isFrom ? "locationFrom" : "locationTo";
      const isHigh = Number(value) >= 6;
      if (Number(next[`${prefix}3`]) > (isHigh ? 18 : 45)) next[`${prefix}3`] = "";
      if (Number(next[`${prefix}4`]) > (isHigh ? 15 : 76)) next[`${prefix}4`] = "";
    }

    const locationIdFrom = valueFormatter.locationIdParser(
      next.locationFrom1, next.locationFrom2, next.locationFrom3, next.locationFrom4, "0"
    );
    const locationIdTo = valueFormatter.locationIdParser(
      next.locationTo1, next.locationTo2, next.locationTo3, next.locationTo4, "9"
    );

    next.locationIdFrom = locationIdFrom;
    next.locationIdTo = locationIdTo;

    updateSearchParams(next, true);
    doSearch(e, next);
  }
 
  async function reqSkuPick() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("지정출고 재고를 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => item.outPossYn == "N");
    if (infos.length > 0) {
      await kutil.alert("작업중인 SKU는 지정출고할 수 없습니다.");
      return;
    }

    if (!state.workStationId) {
      kutil.alert("출고작업대를 선택해 주세요.");
      return;
    }

    for (const info of selectedInfos) {
      info.workStationId = state.workStationId;
    }

    if (await kutil.confirm("지정출고하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/stock_status.proc?mode=reqSkuPick", selectedInfos, {
        validationMsg: validationMsg4reqSkuPick
      });
      await kutil.alert("지정출고하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function addStock() {
    openPopupStockAdd();
  }

  async function updateStockQty() {
    if (result.infos.length == 0) {
      kutil.alert("수정할 재고가 없습니다.");
      return;
    }

    if (await kutil.confirm("재고수정하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/stock_status.proc?mode=updateStockQty", result.infos, {
        validationMsg: validationMsg4updateStockQty
      });
      await kutil.alert("재고수정하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function printLabel() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("라벨 출력 작업을 선택해 주세요.");
      return;
    }

    await printLabelRef.current?.print(selectedInfos);
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/stock/stock_status.download?mode=search", searchParams, {
        "fileName": "재고현황_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function openPopupSearchInput() {
    setState(prev => ({ ...prev, isShowSearchInput: true }));
  }

  function toSummary(csv: string) {
    if (!csv) return "";
    const arr = csv.split(",");
    return arr.length > 1 ? `${arr[0]} 외 ${arr.length - 1}` : arr[0];
  }

  async function closePopupSearchInput(data: any) {
    setState(prev => ({ ...prev, isShowSearchInput: false }));

    if (data?.isSuccess) {
      const p = data.searchParams;
      doSearch(null, {
        pns: p.pns,           pnsSummary: toSummary(p.pns),
        trNos: p.trNos,       trNosSummary: toSummary(p.trNos),
        sns: p.sns,           snsSummary: toSummary(p.sns),
        skuNoExs: p.skuNoExs, skuNoExsSummary: toSummary(p.skuNoExs),
      });
    }
  }

  async function openPopupStockAdd() {
    setState(prev => ({ ...prev, isShowStockAdd: true }));
  }

  async function closePopupStockAdd(data: any) {
    setState(prev => ({ ...prev, isShowStockAdd: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  async function openPopupStockHistory(skuNo: string) {
    setState(prev => ({ ...prev, isShowStockHistory: true, skuNo: skuNo }));
  }

  async function closePopupStockHistory(_data: any) {
    setState(prev => ({ ...prev, isShowStockHistory: false }));
  }

  async function onBarcodeScan(code: string) {
    const [prefix, ...rest] = code.split(":");
    const value = rest.join(":").trim();

    if (rest.length > 0) {
      switch (prefix) {
        case "WS": {
          if (!value) return;
          const exists = (commonCode.workStationId as any[]).some(c => c.code === value);
          if (!exists) {
            kutil.alert("일치하는 작업대가 없습니다.\r\n" + code);
            return;
          }
          setState(prev => ({ ...prev, workStationId: value }));
          return;
        }
        default:
          return;
      }
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list-grid-top">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>재고현황</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
            { auth.download &&
            <Button variant="outlined" onClick={printLabel}>라벨출력</Button>
            }
            { auth.write &&
            <Button variant="outlined" onClick={reqSkuPick}>지정출고</Button>
            }
            { auth.func1 &&
            <Button variant="outlined" onClick={addStock}>재고추가</Button>
            }
            { auth.func2 &&
            <Button variant="contained" onClick={updateStockQty}>재고수정</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>창고</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.wh}
                onChange={(e) => {doSearch(e, {wh: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.wh as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>P/N</p>
            <div className={state.isHoverBtn1 ? "input-btn on" : "input-btn"}>
              <Input
                inputProps={{ readOnly: true, style: { cursor: "pointer" } }}
                value={searchParams.pnsSummary}
                onClick={openPopupSearchInput}
                placeholder="P/N"
              />
              <button
                onMouseEnter={() => setState(prev => ({ ...prev, isHoverBtn1: true }))}
                onMouseLeave={() => setState(prev => ({ ...prev, isHoverBtn1: false }))}
              >
                <Tooltip title="검색어 일괄 입력">
                  <img src={inputIcon} alt="항목입력" onClick={openPopupSearchInput} />
                </Tooltip>
              </button>
            </div>
          </div>
          <div>
            <p>TR No</p>
            <div className={state.isHoverBtn3 ? "input-btn on" : "input-btn"}>
              <Input
                inputProps={{ readOnly: true, style: { cursor: "pointer" } }}
                value={searchParams.trNosSummary}
                onClick={openPopupSearchInput}
                placeholder="TR No"
              />
              <button
                onMouseEnter={() => setState(prev => ({ ...prev, isHoverBtn3: true }))}
                onMouseLeave={() => setState(prev => ({ ...prev, isHoverBtn3: false }))}
              >
                <Tooltip title="검색어 일괄 입력">
                  <img src={inputIcon} alt="항목입력" onClick={openPopupSearchInput} />
                </Tooltip>
              </button>
            </div>
          </div>
          <div>
            <p>S/N</p>
            <div className={state.isHoverBtn2 ? "input-btn on" : "input-btn"}>
              <Input
                inputProps={{ readOnly: true, style: { cursor: "pointer" } }}
                value={searchParams.snsSummary}
                onClick={openPopupSearchInput}
                placeholder="S/N"
              />
              <button
                onMouseEnter={() => setState(prev => ({ ...prev, isHoverBtn2: true }))}
                onMouseLeave={() => setState(prev => ({ ...prev, isHoverBtn2: false }))}
              >
                <Tooltip title="검색어 일괄 입력">
                  <img src={inputIcon} alt="항목입력" onClick={openPopupSearchInput} />
                </Tooltip>
              </button>
            </div>
          </div>
          <div>
            <p>SKU No</p>
            <div className={state.isHoverBtn4 ? "input-btn on" : "input-btn"}>
              <Input
                inputProps={{ readOnly: true, style: { cursor: "pointer" } }}
                value={searchParams.skuNoExsSummary}
                onClick={openPopupSearchInput}
                placeholder="SKU No"
              />
              <button
                onMouseEnter={() => setState(prev => ({ ...prev, isHoverBtn4: true }))}
                onMouseLeave={() => setState(prev => ({ ...prev, isHoverBtn4: false }))}
              >
                <Tooltip title="검색어 일괄 입력">
                  <img src={inputIcon} alt="항목입력" onClick={openPopupSearchInput} />
                </Tooltip>
              </button>
            </div>
          </div>
          <div>
            <p>관리구분</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.mngType}
                onChange={(e) => {doSearch(e, {mngType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.mngType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>재고조정<br />여부</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.stockUpdYn}
                onChange={(e) => {doSearch(e, {stockUpdYn: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.yn as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="select-location">
            <p>Location</p>
            <div>
              <FormControl>
                <Select
                  name="locationFrom1"
                  value={searchParams.locationFrom1}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">SC</MenuItem>
                  {Array.from({ length: kutil.LOCATION_RANGE.level1 }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationFrom2"
                  value={searchParams.locationFrom2}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">열</MenuItem>
                  {Array.from({ length: kutil.LOCATION_RANGE.level2 }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationFrom3"
                  value={searchParams.locationFrom3}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">행</MenuItem>
                  {Array.from({ length: (Number(searchParams.locationFrom1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level3.wp : kutil.LOCATION_RANGE.level3.wb) }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationFrom4"
                  value={searchParams.locationFrom4}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">단</MenuItem>
                  {Array.from({ length: (Number(searchParams.locationFrom1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level4.wp : kutil.LOCATION_RANGE.level4.wb) }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <span>-</span>
              <FormControl>
                <Select
                  name="locationTo1"
                  value={searchParams.locationTo1}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">SC</MenuItem>
                  {Array.from({ length: kutil.LOCATION_RANGE.level1 }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationTo2"
                  value={searchParams.locationTo2}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">열</MenuItem>
                  {Array.from({ length: kutil.LOCATION_RANGE.level2 }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationTo3"
                  value={searchParams.locationTo3}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">행</MenuItem>
                  {Array.from({ length: (Number(searchParams.locationTo1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level3.wp : kutil.LOCATION_RANGE.level3.wb) }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  name="locationTo4"
                  value={searchParams.locationTo4}
                  onChange={changeLocation}
                  displayEmpty
                >
                  <MenuItem value="">단</MenuItem>
                  {Array.from({ length: (Number(searchParams.locationTo1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level4.wp : kutil.LOCATION_RANGE.level4.wb) }, (_, i) => {
                    const v = String(i + 1);
                    return (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 상단 */}
        <div className="grid-top-area">
          <div className="workstation">
            <p>출고작업대</p>
            <FormControl size="small">
              <Select
                value={state.workStationId}
                onChange={(e) => {setState(prev => ({ ...prev, workStationId: e.target.value }))}}
                displayEmpty
              >
              {(commonCode.workStationId as any[]).map((code) => (
                <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
              ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {/*// 그리드 상단 */}
        {/* 그리드 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={defColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              stopEditingWhenCellsLoseFocus={gridOptions.stopEditingWhenCellsLoseFocus}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onCellDoubleClicked={gridCallbacks.onCellDoubleClicked}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 그리드 */}
        {state.isShowSearchInput &&
          <PopupCommon onClose={closePopupSearchInput}>
            <SearchInputPopup data={{searchParams: searchParams}} />
          </PopupCommon>
        }
        {state.isShowStockAdd && <StockAddPopup isShow={state.isShowStockAdd} onClose={closePopupStockAdd} id={props.id} />}
        {state.isShowStockHistory && <StockHistoryPopup isShow={state.isShowStockHistory} onClose={closePopupStockHistory} data={{skuNo: state.skuNo}} />}
        <PrintLabel ref={printLabelRef} />
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(StockStatus);
